import React, { useState, Children, useEffect } from 'react'
import PropTypes from 'prop-types'

import { Box, TableContainer, Table, TableHead, TableBody, TableRow, TableCell, Typography } from '@mui/material'

import { get } from 'utils/lodash'
import { memo } from 'utils/react'
import { handleError } from 'utils/error'
import { handleTooltipOnLargeText } from 'utils/helper'
import { getAddCallFollowUpPayload, getAPIIdParam } from 'utils/form-body'
import { CALLS_PAGINATION } from 'settings/constants/pagination'
import { useLazyGetCustomerCallsQuery } from 'api/calls/getCustomerCalls'
import { useAddUpdateCallFollowUpMutation } from 'api/calls/addUpdateCallFollowUp'
import { useLazyGetCustomerCallByIdQuery } from 'api/calls/getCustomerCallById'
import { sortByDateSelector } from 'data/utils/sortByDateSelector'

import T from 'T'
import Loader from 'components/common/loader'
import HHAccordion from 'components/common/HHAccordion'
import AvatarSquare from 'components/user/AvatarSquare'
import TableSkelton from 'components/common/TableSkelton'
import ConfirmationModal from 'components/modal/ConfirmationModal'
import InteractiveTagsList from 'components/tags/InteractiveTagsList'
import HHSectionPlaceholder from 'components/common/HHSectionPlaceholder'

import CallsFollowUpDrawer from '../drawer/calls-followup'
import CustomerDetailsPaginations from './CustomerDetailsPaginations'
import './common.scss'
import AddCallsFollowupDialog from '../content/AddCallsFollowupDialog'
import { convertDateTimeToPreferedTimezone } from '../../../data/dateTime/convertDateTimeToPreferedTimezone'

const { INITIAL_PAGE, ROWS_PER_PAGE, ROWS_PER_PAGE_OPTIONS } = CALLS_PAGINATION

const CALLS_TABLE_HEADER = [T.DATE, T.ADDED_BY, T.CONTACT, T.SUBJECT, T.DIRECTION, T.TAGS]

const CONFIRM_TITLE = T.FOLLOWUP_CONFIRM_TITLE

const Calls = ({ accountId, locationId = '' }) => {
  const [getCustomerCalls, { isFetching: isCallsLoading, data: callsData }] = useLazyGetCustomerCallsQuery()
  const [addUpdateCallFollowUp, { isLoading: isUpdateCallLoading }] = useAddUpdateCallFollowUpMutation()
  const [getCustomerCallById, { isFetching: isCallByIdLoading }] = useLazyGetCustomerCallByIdQuery()

  const [page, setPage] = useState(INITIAL_PAGE)
  const [rowsPerPage, setRowsPerPage] = useState(ROWS_PER_PAGE)
  const [isDirty, setIsDirty] = useState(false)
  const [isOpenDrawer, setIsOpenDrawer] = useState(false)
  const [singleCall, setSingleCall] = useState(null)
  const [isOpenConfirmModal, setIsOpenConfirmModal] = useState(false)
  const [isOpenAddCallsDialog, setIsOpenAddCallsDialog] = useState(false)

  const { calls: unsortedCalls = [], totalItems: totalCalls = 0, totalPages = 0 } = callsData || {}
  const calls = sortByDateSelector({ data: unsortedCalls, key: 'createdAt' })

  const disabledCallDrawerProceed = !get(singleCall, 'subject', '').trim()

  const getCalls = (requestedPage, requestedPageSize) => {
    const payload = { ...getAPIIdParam(locationId, accountId), requestedPage, requestedPageSize }
    getCustomerCalls(payload).unwrap().catch(handleError)
  }

  // Page change handler
  const handleCallsPageChange = newPage => {
    setPage(newPage)
    getCalls(newPage, rowsPerPage)
  }

  // Rows per page change handler
  const handleCallsRowsPerPageChange = event => {
    setPage(INITIAL_PAGE)
    setRowsPerPage(event.target.value)
    getCalls(INITIAL_PAGE, event.target.value)
  }

  const resetCallsDrawer = () => {
    setSingleCall(null)
    setIsOpenConfirmModal(false)
    setIsOpenDrawer(false)
    setIsDirty(false)
  }

  const handleUpdateCall = () => {
    if (disabledCallDrawerProceed) {
      return
    }

    if (!isDirty) {
      // No need to call API if no changes
      resetCallsDrawer()
      return
    }

    const payload = getAddCallFollowUpPayload(singleCall, accountId, locationId, get(singleCall, 'id'))
    addUpdateCallFollowUp(payload)
      .unwrap()
      .then(() => {
        resetCallsDrawer()
      })
      .catch(handleError)
  }

  const getCallById = callId => {
    if (!callId) {
      return
    }

    getCustomerCallById({ id: callId }, { forceRefetch: true })
      .unwrap()
      .then(res => {
        const callData = get(res, 'call', {})
        if (callData) {
          setIsOpenDrawer(true)
          setSingleCall(callData)
        }
      })
      .catch(handleError)
  }

  useEffect(() => {
    if (accountId || locationId) {
      getCalls(INITIAL_PAGE, rowsPerPage)
    }
  }, [accountId, locationId])

  useEffect(() => handleTooltipOnLargeText())

  return (
    <>
      {(isUpdateCallLoading || isCallByIdLoading) && <Loader />}

      <HHAccordion
        isExpandCollapseHeightSame
        withNewColors
        isExpanded
        summary={T.CALLS}
        actionType="add"
        onActionClick={() => setIsOpenAddCallsDialog(true)}
      >
        <Box className="calls-section common-section">
          <Box className="calls-box common-box common-acc-table">
            <Box className="acc-details common-details">
              {(!Array.isArray(calls) || calls.length === 0) && !isCallsLoading && <HHSectionPlaceholder title={T.NO_CALLS} />}

              <Box display={!isCallsLoading && (!Array.isArray(calls) || calls.length === 0) && 'none'} className="common-table-wrapper">
                <TableContainer className="calls-table common-table">
                  <Table>
                    <TableHead>
                      <TableRow>
                        {Children.toArray(
                          CALLS_TABLE_HEADER.map(header => (
                            <TableCell align={'left'}>
                              <Typography variant="subtitle2">{header}</Typography>
                            </TableCell>
                          ))
                        )}
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {isCallsLoading && <TableSkelton rows={3} columns={5} />}

                      {!isCallsLoading &&
                        Array.isArray(calls) &&
                        Children.toArray(
                          calls.map(call => {
                            const profilePicture = get(call, 'userInfo.profilePicUri', '')
                            const firstName = get(call, 'createdByFirstName', '')
                            const lastName = `${get(call, 'createdByLastName', '')}`

                            const callsContactFirstName = get(call, 'contact.firstName', '')
                            const callsContactLastName = `${get(call, 'contact.lastName', '')}`
                            const callsContactFullName = `${callsContactFirstName} ${callsContactLastName}`.trim()

                            const callCreatedAt = get(call, 'createdAt', '')
                            const { onlyDate, onlyTime } = convertDateTimeToPreferedTimezone({ dateTime: callCreatedAt })

                            return (
                              <TableRow onClick={() => getCallById(get(call, 'id'))} className="cursor-pointer">
                                <TableCell className="date">
                                  {onlyDate}
                                  <br />
                                  <span>{onlyTime}</span>
                                </TableCell>
                                <TableCell>
                                  <AvatarSquare firstName={firstName} lastName={lastName} src={profilePicture} />
                                </TableCell>
                                <TableCell className="contact whitespace-nowrap">{callsContactFullName}</TableCell>
                                <TableCell className="subject">{get(call, 'subject', '')}</TableCell>
                                <TableCell>{get(call, 'direction', '')}</TableCell>
                                <TableCell>
                                  <InteractiveTagsList
                                    accountId={accountId}
                                    isViewOnly={true}
                                    data={{
                                      accountId,
                                      tagDetails: { forAccount: get(call, 'tags', []) },
                                    }}
                                  />
                                </TableCell>
                              </TableRow>
                            )
                          })
                        )}
                    </TableBody>
                  </Table>
                </TableContainer>
              </Box>
              {totalCalls > ROWS_PER_PAGE && (
                <CustomerDetailsPaginations
                  withNewColors
                  page={page}
                  totalCount={totalCalls}
                  rowsPerPage={rowsPerPage}
                  handlePageChange={handleCallsPageChange}
                  rowsPerPageOptions={ROWS_PER_PAGE_OPTIONS}
                  handleRowsPerPageChange={handleCallsRowsPerPageChange}
                  totalPageCount={totalPages}
                />
              )}
            </Box>
          </Box>
        </Box>
      </HHAccordion>

      <CallsFollowUpDrawer
        isOpenDrawer={isOpenDrawer}
        className="calls-drawer"
        details={singleCall}
        onClose={isOpen => {
          if (isDirty) {
            setIsOpenConfirmModal(true)
            return
          }
          setIsOpenDrawer(isOpen)
          setSingleCall(null)
        }}
        onChange={(key, value) => {
          setSingleCall(prev => (prev ? { ...prev, [key]: value } : prev))
          setIsDirty(true)
        }}
        disabledProceed={disabledCallDrawerProceed}
        onProceed={handleUpdateCall}
      />

      <ConfirmationModal
        className="customer-details-slideout-confirm"
        isOpen={isOpenConfirmModal}
        title={CONFIRM_TITLE}
        cancelButtonTitle={T.CANCEL}
        proceedButtonTitle={T.CLOSE}
        onCancel={() => {
          setIsOpenConfirmModal(false)
        }}
        onProceed={() => {
          resetCallsDrawer()
        }}
      />

      {isOpenAddCallsDialog && (
        <AddCallsFollowupDialog
          isOpen={isOpenAddCallsDialog}
          locationId={locationId}
          accountId={accountId}
          onClose={() => setIsOpenAddCallsDialog(false)}
        />
      )}
    </>
  )
}

Calls.propTypes = {
  locationId: PropTypes.string,
  accountId: PropTypes.string.isRequired,
}

export default memo(Calls)
